import React, { useEffect, useRef, useState } from 'react';

// Playback modes for GIF image elements (element.content.gifPlayback):
//   loop  — plays endlessly while the slide is open (default)
//   click — shows the first frame; the teacher clicks to play it once
//   slide — plays once each time the slide opens
export const GIF_PLAYBACK_MODES = ['loop', 'click', 'slide'];

export const isGifSrc = (src) => typeof src === 'string' && /\.gif($|[?#])/i.test(src);

// Browsers can't pause/restart an <img> GIF, so we rewrite the file bytes:
// stripping the NETSCAPE2.0 loop extension makes the GIF play once, inserting
// one with count 0 makes it loop forever, and keeping only the first image
// block gives a still poster. A fresh blob URL restarts the animation.
const NETSCAPE_LOOP_FOREVER = new Uint8Array([
  0x21, 0xff, 0x0b,
  0x4e, 0x45, 0x54, 0x53, 0x43, 0x41, 0x50, 0x45, 0x32, 0x2e, 0x30, // NETSCAPE2.0
  0x03, 0x01, 0x00, 0x00, 0x00,
]);

const skipSubBlocks = (b, p) => {
  while (p < b.length && b[p] !== 0) p += b[p] + 1;
  return p + 1;
};

const parseGif = (b) => {
  if (b.length < 13 || b[0] !== 0x47 || b[1] !== 0x49 || b[2] !== 0x46) return null;
  let pos = 13;
  if (b[10] & 0x80) pos += 3 * (1 << ((b[10] & 7) + 1));
  const headerEnd = pos;
  const blocks = [];
  let duration = 0;
  let firstImageIdx = -1;
  while (pos < b.length) {
    const start = pos;
    if (b[pos] === 0x21) {
      const label = b[pos + 1];
      let kind = 'ext';
      if (label === 0xff && b[pos + 2] === 11) {
        const id = String.fromCharCode(...b.subarray(pos + 3, pos + 14));
        if (id === 'NETSCAPE2.0' || id === 'ANIMEXTS1.0') kind = 'loop';
      } else if (label === 0xf9) {
        const delay = b[pos + 4] | (b[pos + 5] << 8);
        // Browsers clamp delays of 0–1 (1/100 s) to 100ms
        duration += (delay <= 1 ? 10 : delay) * 10;
      }
      pos = skipSubBlocks(b, pos + 2);
      blocks.push({ start, end: pos, kind });
    } else if (b[pos] === 0x2c) {
      let p = pos + 10;
      if (b[pos + 9] & 0x80) p += 3 * (1 << ((b[pos + 9] & 7) + 1));
      pos = skipSubBlocks(b, p + 1);
      if (firstImageIdx < 0) firstImageIdx = blocks.length;
      blocks.push({ start, end: pos, kind: 'image' });
    } else {
      break; // trailer (0x3B) or garbage
    }
    if (pos > b.length) return null;
  }
  if (firstImageIdx < 0) return null;
  return { headerEnd, blocks, firstImageIdx, duration };
};

const buildGif = (b, gif, { loopForever, stillOnly }) => {
  const parts = [b.subarray(0, gif.headerEnd)];
  if (loopForever) parts.push(NETSCAPE_LOOP_FOREVER);
  const last = stillOnly ? gif.firstImageIdx : gif.blocks.length - 1;
  for (let i = 0; i <= last; i++) {
    const blk = gif.blocks[i];
    if (blk.kind !== 'loop') parts.push(b.subarray(blk.start, blk.end));
  }
  parts.push(new Uint8Array([0x3b]));
  return new Blob(parts, { type: 'image/gif' });
};

// Shared per-src so several slides/renders don't refetch or re-split the file.
const variantCache = new Map();
const loadVariants = (url) => {
  if (!variantCache.has(url)) {
    const p = fetch(url)
      .then((r) => { if (!r.ok) throw new Error(`GIF fetch ${r.status}`); return r.arrayBuffer(); })
      .then((buf) => {
        const b = new Uint8Array(buf);
        const gif = parseGif(b);
        if (!gif) throw new Error('Not a GIF');
        return {
          loopUrl: URL.createObjectURL(buildGif(b, gif, { loopForever: true })),
          stillUrl: URL.createObjectURL(buildGif(b, gif, { stillOnly: true })),
          onceBlob: buildGif(b, gif, {}),
          duration: gif.duration,
        };
      });
    p.catch(() => variantCache.delete(url));
    variantCache.set(url, p);
  }
  return variantCache.get(url);
};

// Returns the URL to paint for a GIF in the given mode. playToken bumps
// restart a one-shot playback (click mode). Mounting == slide shown, so
// slide mode plays once on mount.
export const useGifPlayback = (url, mode = 'loop', playToken = 0) => {
  const [variants, setVariants] = useState(null);
  const [failed, setFailed] = useState(false);
  const [onceUrl, setOnceUrl] = useState(null);
  const onceUrlRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setVariants(null);
    setFailed(false);
    if (!url) return undefined;
    loadVariants(url)
      .then((v) => { if (!cancelled) setVariants(v); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [url]);

  useEffect(() => {
    if (!variants) return;
    const shouldPlay = mode === 'slide' || (mode === 'click' && playToken > 0);
    if (!shouldPlay) {
      setOnceUrl(null);
      return;
    }
    const next = URL.createObjectURL(variants.onceBlob);
    if (onceUrlRef.current) URL.revokeObjectURL(onceUrlRef.current);
    onceUrlRef.current = next;
    setOnceUrl(next);
  }, [variants, mode, playToken]);

  useEffect(() => () => {
    if (onceUrlRef.current) URL.revokeObjectURL(onceUrlRef.current);
  }, []);

  if (failed) return url;
  if (!variants) return mode === 'loop' ? url : null;
  if (mode === 'loop') return variants.loopUrl;
  return onceUrl || variants.stillUrl;
};

// Background-image layer for image elements. Non-GIFs render as before.
// In click mode the layer becomes clickable for whoever `canTrigger`; in a
// live lesson `syncedPlay` ({ at }) / `onPlay` mirror the teacher's click to
// students via interactive state.
const SYNC_WINDOW_MS = 10000;

const GifImageLayer = ({
  src,
  mode,
  canTrigger = false,
  syncedPlay = null,
  onPlay = null,
  className = 'absolute inset-0 pointer-events-none',
  style,
}) => {
  const isGif = isGifSrc(src);
  const effectiveMode = GIF_PLAYBACK_MODES.includes(mode) ? mode : 'loop';
  const [playToken, setPlayToken] = useState(0);
  const lastAppliedAt = useRef(syncedPlay?.at || 0);

  useEffect(() => {
    const at = syncedPlay?.at;
    if (!at || at === lastAppliedAt.current) return;
    lastAppliedAt.current = at;
    // Ignore stale plays (e.g. state fetched when re-entering the slide)
    if (Math.abs(Date.now() - at) > SYNC_WINDOW_MS) return;
    setPlayToken((t) => t + 1);
  }, [syncedPlay?.at]);

  const displayUrl = useGifPlayback(isGif ? src : null, effectiveMode, playToken);
  const url = isGif ? displayUrl : src;
  const clickable = isGif && effectiveMode === 'click' && canTrigger;

  const handleClick = () => {
    const at = Date.now();
    lastAppliedAt.current = at;
    setPlayToken((t) => t + 1);
    if (onPlay) onPlay({ at });
  };

  return (
    <div
      className={className}
      onClick={clickable ? handleClick : undefined}
      style={{
        ...style,
        backgroundImage: url ? `url(${url})` : 'none',
        ...(clickable && { pointerEvents: 'auto', cursor: 'pointer' }),
      }}
    />
  );
};

export default GifImageLayer;
